import React, { useEffect, useRef, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  DatePicker,
  Popconfirm,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';

const { Option } = Select;

export interface RedemptionBookingRecord {
  recId: string;
  clientId: string;
  advisorId: string;
  emdId: string;
  fullName?: string;
  clientName: string;
  account: string;
  advisorName: string;
  emdFirm: string;
  amount: number;
  dateRequest: string;
  dateSchedule: string;
  queueNumber: number;
  status: string;
}

// Bookings only ever sit in these two states; once promoted the row moves to
// the redemption list and is deleted here.
export const BOOKING_STATUS_OPTIONS = ['Pending', 'Cancelled'];

export const STATIC_EMD_OPTIONS = [
  'Enoch Wealth Inc.',
  'IBP Capital Inc',
  'Raisen Pacific Wealth',
  'Waverley',
  'Tyche Global Financial',
  'Heng Wealth Management Corp',
  'Higgins Investment Group Inc',
  'Equivesto',
];

// Mirrors assertSchedulableMonth() on the server: the earliest bookable month
// is M+2, computed in UTC so both sides agree on the boundary.
export const minSchedulableMonth = (): string => {
  const now = new Date();
  const floor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 2, 1));
  return `${floor.getUTCFullYear()}-${String(floor.getUTCMonth() + 1).padStart(2, '0')}`;
};

interface RedemptionBookingTableProps {
  records: RedemptionBookingRecord[];
  loading?: boolean;
  editable?: boolean;
  centerHeaders?: boolean;
  clientNameWrapChars?: number;
  advisorWrapChars?: number;
  emdWrapChars?: number;
  addButtonLabel?: string;
  onSave?: (
    recId: string,
    patch: Partial<RedemptionBookingRecord>,
  ) => Promise<boolean | void> | boolean | void;
  onAdd?: () => string | void | Promise<string | void>;
  onDelete?: (recId: string) => Promise<boolean | void> | boolean | void;
  onAccountLookup?: (
    recId: string,
    account: string,
  ) =>
    | Promise<Partial<RedemptionBookingRecord> | void>
    | Partial<RedemptionBookingRecord>
    | void;
}

type EditableField =
  | 'clientName'
  | 'account'
  | 'advisorName'
  | 'advisorId'
  | 'emdFirm'
  | 'emdId'
  | 'amount'
  | 'dateRequest'
  | 'dateSchedule'
  | 'status';

const formatCurrency = (value: number): string =>
  `$${(value || 0).toLocaleString('en-US')}`;

const formatDateYMD = (value?: string): string => {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : value;
};

const formatMonth = (value?: string): string => {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM') : value;
};

const wrapLongText = (value: string, wrapChars?: number): React.ReactNode => {
  if (!value || !wrapChars || wrapChars <= 0 || value.length <= wrapChars) {
    return value;
  }

  return (
    <div
      style={{
        maxWidth: `${wrapChars}ch`,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      }}
      title={value}
    >
      {value}
    </div>
  );
};

// Maps a raw account number to its registered-plan type for display.
const accountType = (account?: string): string => {
  const acc = account || '';
  if (acc.startsWith('ONRS') || acc.startsWith('BCRS')) {
    return 'RRSP';
  } else if (acc.startsWith('ONRE') || acc.startsWith('BCRE')) {
    return 'RESP';
  } else if (acc.startsWith('ONRF') || acc.startsWith('BCRF')) {
    return 'RRIF';
  } else if (acc.startsWith('ONSR')) {
    return 'SPRRSP';
  } else if (acc.startsWith('ONTF') || acc.startsWith('BCTF')) {
    return 'TFSA';
  } else if (acc.startsWith('BCLR')) {
    return 'LIRA';
  } else {
    return 'Non-Register';
  }
};

const RedemptionBookingTable: React.FC<RedemptionBookingTableProps> = ({
  records,
  loading = false,
  editable = false,
  centerHeaders = false,
  clientNameWrapChars,
  advisorWrapChars,
  emdWrapChars,
  addButtonLabel = 'New Record',
  onSave,
  onAdd,
  onDelete,
  onAccountLookup,
}) => {
  const pageSize = 10;
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<RedemptionBookingRecord>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const saveInFlightRef = useRef<boolean>(false);
  const accountLookupInFlightRef = useRef<Promise<Partial<RedemptionBookingRecord> | void> | null>(
    null,
  );
  const lastPage = Math.max(1, Math.ceil(records.length / pageSize));

  useEffect(() => {
    if (editingKey && !records.some((row) => row.recId === editingKey)) {
      setEditingKey(null);
      setDraft({});
      setSaving(false);
      saveInFlightRef.current = false;
    }

    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [records, editingKey, currentPage, lastPage]);

  const isEditing = (record: RedemptionBookingRecord) =>
    record.recId === editingKey;

  const isNewRow = (record: RedemptionBookingRecord) =>
    record.recId.startsWith('new-');

  const startEdit = (record: RedemptionBookingRecord) => {
    setDraft({
      clientName: record.clientName,
      account: record.account,
      advisorName: record.advisorName,
      advisorId: record.advisorId,
      emdFirm: record.emdFirm,
      emdId: record.emdId,
      amount: record.amount,
      dateRequest: formatDateYMD(record.dateRequest),
      dateSchedule: formatMonth(record.dateSchedule),
      status: record.status,
    });
    setEditingKey(record.recId);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft({});
  };

  const setField = (field: EditableField, value: any) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const lookupAccount = (record: RedemptionBookingRecord) => {
    if (!onAccountLookup) return;
    const lookupPromise = Promise.resolve(
      onAccountLookup(record.recId, (draft.account as string) || ''),
    ).then((lookupPatch) => {
      if (lookupPatch) {
        setDraft((prev) => ({ ...prev, ...lookupPatch }));
      }
      return lookupPatch;
    });
    accountLookupInFlightRef.current = lookupPromise;
    void lookupPromise.finally(() => {
      if (accountLookupInFlightRef.current === lookupPromise) {
        accountLookupInFlightRef.current = null;
      }
    });
    return lookupPromise;
  };

  const saveEdit = async (record: RedemptionBookingRecord) => {
    if (saveInFlightRef.current) return;
    if (!onSave) {
      cancelEdit();
      return;
    }
    saveInFlightRef.current = true;
    setSaving(true);
    try {
      const lookupPatch = isNewRow(record)
        ? await accountLookupInFlightRef.current
        : undefined;
      const effectiveDraft = lookupPatch ? { ...draft, ...lookupPatch } : draft;
      const editableFields: EditableField[] = isNewRow(record)
        ? [
            'clientName',
            'account',
            'advisorName',
            'advisorId',
            'emdFirm',
            'emdId',
            'amount',
            'dateRequest',
            'dateSchedule',
            'status',
          ]
        : ['advisorName', 'emdFirm', 'amount', 'dateSchedule', 'status'];
      const patch: Partial<RedemptionBookingRecord> = {};

      const normalizedValue = (field: EditableField): any => {
        const value = effectiveDraft[field];
        if (
          isNewRow(record) &&
          (field === 'advisorName' ||
            field === 'advisorId' ||
            field === 'emdFirm' ||
            field === 'emdId') &&
          (value === undefined || value === null || value === '')
        ) {
          return (record as any)[field];
        }
        return value;
      };

      editableFields.forEach((field) => {
        const value = normalizedValue(field);
        // dateSchedule is held month-granular in the draft but date-granular on
        // the record, so compare like for like.
        const currentValue =
          field === 'dateSchedule'
            ? formatMonth(record.dateSchedule)
            : (record as any)[field];
        if (value !== undefined && value !== currentValue) {
          (patch as any)[field] = value;
        }
      });

      if (Object.keys(patch).length === 0) {
        cancelEdit();
        return;
      }

      const saved = await onSave(record.recId, patch);
      if (saved !== false) {
        cancelEdit();
      }
    } finally {
      setSaving(false);
      saveInFlightRef.current = false;
    }
  };

  const handleDelete = async (record: RedemptionBookingRecord) => {
    if (!onDelete) return;
    setDeletingKey(record.recId);
    try {
      await onDelete(record.recId);
    } finally {
      setDeletingKey(null);
    }
  };

  const columns: any[] = [
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      onHeaderCell: () => ({ style: { textAlign: 'left' } }),
      onCell: () => ({ style: { textAlign: 'left' } }),
      render: (clientName: string, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record) && isNewRow(record)) {
          return (
            <Input
              value={draft.clientName as string}
              style={{ width: 180 }}
              placeholder="Enter full name"
              onChange={(e) => setField('clientName', e.target.value)}
            />
          );
        }
        return wrapLongText(clientName || '', clientNameWrapChars);
      },
    },
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (account: string, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record) && isNewRow(record)) {
          return (
            <Input
              value={draft.account as string}
              style={{ width: 140 }}
              placeholder="Enter account"
              onChange={(e) => setField('account', e.target.value)}
              onBlur={() => void lookupAccount(record)}
              onPressEnter={() => void lookupAccount(record)}
            />
          );
        }
        return accountType(account);
      },
    },
    {
      title: 'Advisor',
      dataIndex: 'advisorName',
      key: 'advisorName',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (advisorName: string, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Input
              value={(draft.advisorName as string) || ''}
              style={{ width: 180 }}
              placeholder="Enter advisor name"
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  advisorName: e.target.value,
                  advisorId: '',
                }))
              }
            />
          );
        }
        return wrapLongText(advisorName || '', advisorWrapChars);
      },
    },
    {
      title: 'EMD',
      dataIndex: 'emdFirm',
      key: 'emdFirm',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (emdFirm: string, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Select
              value={draft.emdFirm as string}
              style={{ minWidth: 180 }}
              placeholder="Select EMD"
              onChange={(val) =>
                setDraft((prev) => ({ ...prev, emdFirm: val, emdId: '' }))
              }
            >
              {STATIC_EMD_OPTIONS.map((firm) => (
                <Option key={firm} value={firm}>
                  {firm}
                </Option>
              ))}
            </Select>
          );
        }
        return wrapLongText(emdFirm || '', emdWrapChars);
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (amount: number, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Input
              value={draft.amount !== undefined ? String(draft.amount) : ''}
              style={{ width: 130 }}
              placeholder="Enter amount"
              onChange={(e) => setField('amount', e.target.value)}
            />
          );
        }
        return formatCurrency(amount);
      },
    },
    {
      title: 'Date Request',
      dataIndex: 'dateRequest',
      key: 'dateRequest',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (dateRequest: string, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record) && isNewRow(record)) {
          return (
            <Input
              type="date"
              value={(draft.dateRequest as string) || formatDateYMD(dateRequest)}
              style={{ width: 150 }}
              onChange={(e) => setField('dateRequest', e.target.value)}
            />
          );
        }
        return formatDateYMD(dateRequest);
      },
    },
    {
      title: 'Redemp Schedule',
      dataIndex: 'dateSchedule',
      key: 'dateSchedule',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (dateSchedule: string, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record)) {
          const minMonth = minSchedulableMonth();
          const value = draft.dateSchedule as string;
          return (
            <DatePicker
              picker="month"
              format="YYYY-MM"
              allowClear={false}
              style={{ width: 140 }}
              placeholder={minMonth}
              value={value ? dayjs(value) : null}
              disabledDate={(current) => current.format('YYYY-MM') < minMonth}
              onChange={(next: any) =>
                setField('dateSchedule', next ? next.format('YYYY-MM') : '')
              }
            />
          );
        }
        return formatMonth(dateSchedule);
      },
    },
    {
      title: 'Queue Number',
      dataIndex: 'queueNumber',
      key: 'queueNumber',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (status: string, record: RedemptionBookingRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Select
              value={draft.status as string}
              style={{ minWidth: 130 }}
              onChange={(val) => setField('status', val)}
            >
              {BOOKING_STATUS_OPTIONS.map((opt) => (
                <Option key={opt} value={opt}>
                  {opt}
                </Option>
              ))}
            </Select>
          );
        }
        const statusColor =
          status === 'Pending'
            ? '#ff4d4f'
            : status === 'Cancelled'
              ? '#000000'
              : undefined;
        return <span style={{ color: statusColor }}>{status}</span>;
      },
    },
  ];

  if (editable) {
    columns.push({
      title: 'Action',
      key: 'action',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (_: any, record: RedemptionBookingRecord) => {
        if (isEditing(record)) {
          return (
            <Space>
              <Button
                type="link"
                size="small"
                loading={saving}
                disabled={saving}
                onClick={() => saveEdit(record)}
              >
                Save
              </Button>
              <Button type="link" size="small" onClick={cancelEdit}>
                Cancel
              </Button>
            </Space>
          );
        }

        // The server only accepts a delete once the booking has been cancelled.
        const deletable = record.status === 'Cancelled' && !isNewRow(record);
        const deleteButton = (
          <Button
            type="link"
            size="small"
            danger
            loading={deletingKey === record.recId}
            disabled={!deletable || editingKey !== null}
          >
            Delete
          </Button>
        );
        return (
          <Space>
            <Button
              type="link"
              size="small"
              disabled={editingKey !== null || deletingKey !== null}
              onClick={() => startEdit(record)}
            >
              Edit
            </Button>
            {onDelete &&
              (deletable ? (
                <Popconfirm
                  title="Delete this booking?"
                  okText="Delete"
                  cancelText="Cancel"
                  disabled={editingKey !== null}
                  onConfirm={() => handleDelete(record)}
                >
                  {deleteButton}
                </Popconfirm>
              ) : (
                <Tooltip title="Set status to Cancelled before deleting">
                  {deleteButton}
                </Tooltip>
              ))}
          </Space>
        );
      },
    });
  }

  const handleAdd = async () => {
    if (!onAdd) return;
    const newRecId = await onAdd();
    if (typeof newRecId === 'string') {
      setDraft({
        clientName: '',
        account: '',
        emdFirm: '',
        dateSchedule: minSchedulableMonth(),
        status: 'Pending',
      });
      setEditingKey(newRecId);
    }
  };

  const showAddFooter = !!onAdd && Math.min(currentPage, lastPage) === lastPage;

  return (
    <Table
      rowKey="recId"
      loading={loading}
      dataSource={records}
      columns={columns}
      pagination={{ pageSize, hideOnSinglePage: true }}
      onChange={(pagination) => setCurrentPage(pagination.current || 1)}
      size="middle"
      footer={
        showAddFooter
          ? () => (
              <Button
                type="dashed"
                block
                disabled={editingKey !== null}
                onClick={handleAdd}
              >
                {addButtonLabel}
              </Button>
            )
          : undefined
      }
    />
  );
};

export default RedemptionBookingTable;
